import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Search, Phone, MapPin, Calendar, DollarSign, Upload, X, Eye, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Clock, User, CreditCard, FileText, Camera } from 'lucide-react';

interface Client {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  address: string;
  business_address: string;
  code: string;
  seller_name: string;
  ruta: number;
  outstanding_balance: number;
  pendingInstallments: Installment[];
  paidInstallments: Installment[];
  installmentsLoaded: boolean;
}

interface Installment {
  id: string;
  due_date: string;
  amount: number;
  status: string;
  paid_amount?: number;
  late_fee?: number;
  paid_at?: string;
  bank_name?: string;
  proof_file_url?: string[];
  payment_date_time?: string;
  is_checked?: boolean;
  checked_by?: string;
  checked_at?: string;
}

export default function CollectionsV2() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [cpfSearchTerm, setCpfSearchTerm] = useState('');
  const [codeSearchTerm, setCodeSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedInstallment, setSelectedInstallment] = useState<Installment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [lateFee, setLateFee] = useState('');
  const [bankName, setBankName] = useState('');
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const [paymentDateTime, setPaymentDateTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate that search term is provided
      if (!nameSearchTerm.trim() && !cpfSearchTerm.trim() && !codeSearchTerm.trim()) {
        setError('Por favor, digite um termo de busca para encontrar clientes.');
        setLoading(false);
        return;
      }

      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          address,
          business_address,
          code,
          ruta,
          outstanding_balance,
          seller:users!contracts_seller_id_fkey(email)
        `)
        .eq('approval_status', 'approved')
        .neq('status', 'completed')
        .limit(100);

      if (user?.unit_id) {
        query = query.eq('unit_id', user.unit_id);
      }
      
      // Apply search filter
      const filters = [];
      if (nameSearchTerm.trim()) {
        filters.push(`client_name.ilike.%${nameSearchTerm.trim()}%`);
      }
      if (cpfSearchTerm.trim()) {
        const cleanCpf = cpfSearchTerm.replace(/\D/g, '');
        filters.push(`cpf.ilike.%${cleanCpf}%`);
      }
      if (codeSearchTerm.trim()) {
        filters.push(`code.ilike.%${codeSearchTerm.trim()}%`);
      }
      
      if (filters.length > 0) {
        query = query.or(filters.join(','));
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      const clientsWithInstallments = (data || []).map(contract => ({
        id: contract.id,
        client_name: contract.client_name,
        cpf: contract.cpf,
        phone: contract.phone,
        address: contract.address,
        business_address: contract.business_address,
        code: contract.code,
        seller_name: contract.seller?.email || 'N/A',
        ruta: contract.ruta,
        outstanding_balance: contract.outstanding_balance || 0,
        pendingInstallments: [],
        paidInstallments: [],
        installmentsLoaded: false,
      }));

      setClients(clientsWithInstallments);
      setSearched(true);
      loadAllInstallments(clientsWithInstallments.map(c => c.id));
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadAllInstallments = async (contractIds: string[]) => {
    if (contractIds.length === 0) return;
    const { data } = await supabase
      .from('installments')
      .select(`
        id, contract_id, due_date, amount, status, paid_amount, late_fee,
        paid_at, bank_name, proof_file_url, payment_date_time,
        is_checked, checked_by, checked_at
      `)
      .in('contract_id', contractIds)
      .order('due_date', { ascending: true });

    if (!data) return;

    const byContract = new Map<string, Installment[]>();
    data.forEach((inst: any) => {
      if (!byContract.has(inst.contract_id)) byContract.set(inst.contract_id, []);
      byContract.get(inst.contract_id)!.push(inst);
    });

    setClients(prev => prev.map(c => {
      const installments = byContract.get(c.id) || [];
      return {
        ...c,
        installmentsLoaded: true,
        pendingInstallments: installments
          .filter(i => i.status === 'pending' || i.status === 'overdue' || i.status === 'partial'),
        paidInstallments: installments
          .filter(i => i.status === 'paid')
          .sort((a, b) => new Date(b.paid_at || b.payment_date_time || '').getTime() - new Date(a.paid_at || a.payment_date_time || '').getTime()),
      };
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };
  
  const handleClearSearch = () => {
    setNameSearchTerm('');
    setCpfSearchTerm('');
    setCodeSearchTerm('');
    setClients([]);
    setSearched(false);
    setError(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const isOverdue = (dueDate: string) => {
    const dueDateObj = new Date(dueDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDateObj < today;
  };

  const handlePaymentSubmit = async () => {
    if (!selectedClient || !selectedInstallment || !paymentAmount) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    // Helper function to sanitize file names for storage
    const sanitizeFileName = (fileName: string) => {
      const lastDotIndex = fileName.lastIndexOf('.');
      const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
      const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
      
      // Replace non-alphanumeric characters with underscores, keep only letters, numbers, hyphens and underscores
      const sanitizedName = name.replace(/[^a-zA-Z0-9\-_]/g, '_');
      return sanitizedName + extension;
    };

    try {
      setSubmitting(true);

      const amount = parseFloat(paymentAmount.replace(',', '.'));
      const fee = lateFee && lateFee.trim() ? parseFloat(lateFee.replace(',', '.')) : null;

      // Fetch current installment data to get existing paid_amount
      const { data: currentInstallment, error: fetchError } = await supabase
        .from('installments')
        .select('paid_amount, amount')
        .eq('id', selectedInstallment.id)
        .single();

      if (fetchError) throw fetchError;

      // Calculate new total paid amount by adding current payment to existing paid amount
      const currentPaidAmount = currentInstallment.paid_amount || 0;
      const newTotalPaidAmount = currentPaidAmount + amount;
      
      // Determine if installment is now fully paid
      const isFullyPaid = newTotalPaidAmount >= currentInstallment.amount;
      const newStatus = isFullyPaid ? 'paid' : 'partial';

      // Convert payment datetime to UTC to avoid timezone issues
      let paymentDateTimeUTC = null;
      if (paymentDateTime) {
        // Create date object from the datetime-local input (which is in local timezone)
        const localDate = new Date(paymentDateTime);
        // Convert to UTC ISO string
        paymentDateTimeUTC = localDate.toISOString();
      }

      // Upload proof files if any
      let proofUrls: string[] = [];
      if (proofFiles.length > 0) {
        try {
          for (const file of proofFiles) {
            const sanitizedName = sanitizeFileName(file.name);
            const fileName = `${Date.now()}-${sanitizedName}`;
            const filePath = `payment-proofs/${selectedInstallment.id}/${fileName}`;

            // Use signed URL upload method for better content type handling
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
              .from('contract-files')
              .createSignedUploadUrl(filePath);

            if (signedUrlError || !signedUrlData?.signedUrl) {
              console.warn('Failed to get signed upload URL:', signedUrlError?.message);
              // Continue without uploading files if bucket doesn't exist
              break;
            }

            // Upload file using signed URL with explicit content type
            const response = await fetch(signedUrlData.signedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': file.type
              },
              body: file
            });

            if (!response.ok) {
              console.warn('Failed to upload proof file:', response.statusText);
              break;
            }

            const { data: { publicUrl } } = supabase.storage
              .from('contract-files')
              .getPublicUrl(filePath);

            proofUrls.push(publicUrl);
          }
        } catch (storageError) {
          console.warn('Storage upload failed, continuing without proof files:', storageError);
          // Continue processing payment even if file upload fails
        }
      }

      // Create payment transaction record
      const { error: transactionError } = await supabase
        .from('payment_transactions')
        .insert({
          installment_id: selectedInstallment.id,
          contract_id: selectedClient.id,
          amount: amount,
          late_fee: fee || 0,
          bank_name: bankName || null,
          proof_file_url: proofUrls.length > 0 ? proofUrls : null,
          payment_date_time: paymentDateTimeUTC,
          registered_by: user?.id,
          registered_at: new Date().toISOString(),
          status: 'pending_verification',
          unit_id: user?.unit_id
        });

      if (transactionError) {
        throw transactionError;
      }

      // Update installment with optimistic values (will be recalculated if rejected)
      const { error: updateError } = await supabase
        .from('installments')
        .update({
          status: newStatus,
          paid_amount: newTotalPaidAmount,
          late_fee: fee,
          paid_at: new Date().toISOString(),
          bank_name: bankName || null,
          proof_file_url: proofUrls.length > 0 ? proofUrls : null,
          payment_date_time: paymentDateTimeUTC
        })
        .eq('id', selectedInstallment.id);

      if (updateError) {
        throw updateError;
      }

      // Get current outstanding balance from the contract
      const { data: contractData, error: contractFetchError } = await supabase
        .from('contracts')
        .select('outstanding_balance')
        .eq('id', selectedClient.id)
        .single();

      if (contractFetchError) {
        throw contractFetchError;
      }

      // Calculate new outstanding balance
      const currentOutstandingBalance = contractData.outstanding_balance || 0;
      const newOutstandingBalance = Math.max(0, currentOutstandingBalance - amount);
      const newBalance = Math.max(0, currentOutstandingBalance - amount);

      // Update contract outstanding balance
      const { error: contractError } = await supabase
        .from('contracts')
        .update({ outstanding_balance: newBalance })
        .eq('id', selectedClient.id);

      if (contractError) {
        throw contractError;
      }

      // NOTE: Payments are now tracked exclusively through payment_transactions table
      // Do NOT create cash_movements entries for payments to avoid double-counting
      // The cashbox already sums payments from payment_transactions

      console.log(`✅ Pagamento registrado com sucesso!`);
      console.log(`   Cliente: ${selectedClient.client_name}`);
      console.log(`   Valor pago nesta transação: R$ ${amount.toFixed(2)}`);
      console.log(`   Valor pago anteriormente: R$ ${currentPaidAmount.toFixed(2)}`);
      console.log(`   Novo total pago na parcela: R$ ${newTotalPaidAmount.toFixed(2)}`);
      console.log(`   Valor total da parcela: R$ ${currentInstallment.amount.toFixed(2)}`);
      console.log(`   Status da parcela: ${newStatus}`);
      console.log(`   Juros: R$ ${fee ? fee.toFixed(2) : 'null'}`);
      console.log(`   Saldo devedor anterior: R$ ${currentOutstandingBalance.toFixed(2)}`);
      console.log(`   Novo saldo devedor: R$ ${newOutstandingBalance.toFixed(2)}`);

      alert(`Pagamento registrado com sucesso!\n\nParcela: ${isFullyPaid ? 'QUITADA' : 'PARCIAL'}\nTotal pago na parcela: ${formatCurrency(newTotalPaidAmount)}\nNovo saldo devedor: ${formatCurrency(newOutstandingBalance)}`);
      setShowPaymentModal(false);
      resetPaymentForm();
      fetchClients();
    } catch (err) {
      console.error('Error processing payment:', err);
      alert('Erro ao processar pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount('');
    setLateFee('');
    setBankName('');
    setProofFiles([]);
    setPaymentDateTime('');
    setSelectedClient(null);
    setSelectedInstallment(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setProofFiles(Array.from(e.target.files));
    }
  };

  const openPaymentModal = (client: Client, installment: Installment) => {
    setSelectedClient(client);
    setSelectedInstallment(installment);
    setPaymentAmount(installment.amount.toString());
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setPaymentDateTime(localDateTime);
    setShowPaymentModal(true);
  };

  const openDetailsModal = (client: Client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erro</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Cobrança V2</h1>
        <button
          onClick={fetchClients}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          Atualizar
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-blue-600" />
          <h2 className="text-base lg:text-lg font-semibold text-gray-800">Buscar Clientes para Cobrança</h2>
        </div>
        
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Cliente
              </label>
              <input
                type="text"
                value={nameSearchTerm}
                onChange={(e) => setNameSearchTerm(e.target.value)}
                placeholder="Digite o nome do cliente..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF
              </label>
              <input
                type="text"
                value={cpfSearchTerm}
                onChange={(e) => setCpfSearchTerm(e.target.value)}
                placeholder="000.000.000-00"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código do Contrato
              </label>
              <input
                type="text"
                value={codeSearchTerm}
                onChange={(e) => setCodeSearchTerm(e.target.value)}
                placeholder="Digite o código..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
            <button
              type="submit"
              disabled={loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim() && !codeSearchTerm.trim())}
              className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white transition-colors text-sm lg:text-base ${
                loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim() && !codeSearchTerm.trim())
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Buscando...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Buscar Clientes</span>
                </>
              )}
            </button>
            
            {(nameSearchTerm.trim() || cpfSearchTerm.trim() || codeSearchTerm.trim()) && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base"
              >
                Limpar Filtros
              </button>
            )}
          </div>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Buscando clientes...</span>
          </div>
        ) : !searched ? (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Busque por clientes</h3>
            <p className="text-gray-500">
              Use os filtros acima para encontrar clientes pelo nome, CPF ou código.
              <br />
              Digite pelo menos um dos campos para iniciar a busca.
            </p>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Nenhum cliente encontrado</h3>
            <p className="text-gray-500">
              Não foram encontrados clientes com os filtros aplicados.
              <br />
              Tente ajustar os termos de busca.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base lg:text-lg font-semibold text-gray-800">
                Resultados da Busca ({clients.length} cliente{clients.length !== 1 ? 's' : ''})
              </h2>
            </div>

            <div className="grid gap-4">
              {clients.map((client) => (
                <div key={client.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{client.client_name}</h3>
                      <p className="text-sm text-gray-600">Código: {client.code}</p>
                      <p className="text-sm text-gray-600">CPF: {formatCPF(client.cpf)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Saldo Devedor</p>
                      <p className="text-lg font-bold text-red-600">
                        {formatCurrency(client.outstanding_balance)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      {formatPhone(client.phone)}
                    </div>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      Ruta {client.ruta} — {client.seller_name}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <h4 className="font-medium text-gray-900">Parcelas Pendentes:</h4>
                    {client.pendingInstallments.map((installment) => (
                      <div
                        key={installment.id}
                        className={`flex justify-between items-center p-3 rounded-md ${
                          isOverdue(installment.due_date)
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">
                            Vencimento: {formatDate(installment.due_date)}
                          </span>
                          {isOverdue(installment.due_date) && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                              Em atraso
                            </span>
                          )}
                          {installment.status === 'partial' && installment.paid_amount && installment.paid_amount > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Pagamento Parcial
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          {installment.paid_amount && installment.paid_amount > 0 ? (
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                Total: {formatCurrency(installment.amount)}
                              </div>
                              <div className="text-xs text-green-600">
                                Pago: {formatCurrency(installment.paid_amount || 0)}
                              </div>
                              <div className="text-xs text-red-600 font-medium">
                                Restante: {formatCurrency(installment.amount - (installment.paid_amount || 0))}
                              </div>
                            </div>
                          ) : (
                            <span className="font-medium">
                              {formatCurrency(installment.amount)}
                            </span>
                          )}
                          <button
                            onClick={() => openPaymentModal(client, installment)}
                            className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors"
                          >
                            {installment.paid_amount && installment.paid_amount > 0 ? 'Completar Pagamento' : 'Registrar Pagamento'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Parcelas Pagas */}
                  {client.paidInstallments.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <h4 className="font-medium text-gray-900">Parcelas Pagas:</h4>
                      {client.paidInstallments.map((installment) => (
                        <div
                          key={installment.id}
                          className="flex justify-between items-center p-3 rounded-md bg-green-50 border border-green-200"
                        >
                          <div className="flex items-center space-x-3">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <div>
                              {installment.status === 'partial' ? (
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-600">
                                    Valor Total: {formatCurrency(installment.amount)}
                                  </p>
                                  <p className="text-xs text-green-600">
                                    Já Pago: {formatCurrency(installment.paid_amount || 0)}
                                  </p>
                                  <p className="text-xs text-red-600 font-medium">
                                    Restante: {formatCurrency(installment.amount - (installment.paid_amount || 0))}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">
                                  Valor: {formatCurrency(installment.amount)}
                                </p>
                              )}
                              <div className="text-xs text-green-700">
                                Pago em: {installment.paid_at ? 
                                  formatDate(installment.paid_at.split('T')[0]) : 
                                  installment.payment_date_time ? 
                                    formatDate(installment.payment_date_time.split('T')[0]) : 
                                    'Data não informada'
                                }
                                {installment.status === 'partial' && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full mb-1 block">
                                    Pagamento Parcial
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="font-medium text-green-600">
                              {formatCurrency(installment.paid_amount || installment.amount)}
                            </span>
                            {installment.late_fee && installment.late_fee > 0 && (
                              <div className="text-xs text-orange-600">
                                + {formatCurrency(installment.late_fee)} (juros)
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => openDetailsModal(client)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Ver detalhes completos
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedClient && selectedInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Registrar Pagamento</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Cliente: {selectedClient.client_name}</p>
                <p className="text-sm text-gray-600">
                  Vencimento: {formatDate(selectedInstallment.due_date)}
                </p>
                {selectedInstallment.status === 'partial' ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      Valor Total da Parcela: {formatCurrency(selectedInstallment.amount)}
                    </p>
                    <p className="text-sm text-green-600">
                      Já Pago: {formatCurrency(selectedInstallment.paid_amount || 0)}
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                      Restante a Pagar: {formatCurrency(selectedInstallment.amount - (selectedInstallment.paid_amount || 0))}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Valor da Parcela: {formatCurrency(selectedInstallment.amount)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Pago *
                </label>
                {selectedInstallment && selectedInstallment.status === 'partial' && (
                  <p className="text-xs text-blue-600 mb-1">
                    Restante a pagar: {formatCurrency(selectedInstallment.amount - (selectedInstallment.paid_amount || 0))}
                  </p>
                )}
                <input
                  type="text"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={selectedInstallment && selectedInstallment.status === 'partial' ? 
                    formatCurrency(selectedInstallment.amount - (selectedInstallment.paid_amount || 0)).replace('R$', '').trim() : 
                    "0,00"
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taxa de Atraso
                </label>
                <input
                  type="text"
                  value={lateFee}
                  onChange={(e) => setLateFee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco
                </label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome do banco"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data/Hora do Pagamento
                </label>
                <input
                  type="datetime-local"
                  value={paymentDateTime}
                  onChange={(e) => setPaymentDateTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comprovantes
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {proofFiles.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {proofFiles.length} arquivo(s) selecionado(s)
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Processando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Detalhes do Cliente</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Informações Pessoais</h4>
                  <p className="text-sm text-gray-600">Nome: {selectedClient.client_name}</p>
                  <p className="text-sm text-gray-600">CPF: {formatCPF(selectedClient.cpf)}</p>
                  <p className="text-sm text-gray-600">Telefone: {formatPhone(selectedClient.phone)}</p>
                  <p className="text-sm text-gray-600">Código: {selectedClient.code}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Informações do Contrato</h4>
                  <p className="text-sm text-gray-600">Vendedor: {selectedClient.seller_name}</p>
                  <p className="text-sm text-gray-600">Ruta: {selectedClient.ruta}</p>
                  <p className="text-sm text-gray-600">
                    Saldo Devedor: {formatCurrency(selectedClient.outstanding_balance)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Endereços</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 text-gray-500 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Residencial:</p>
                      <p className="text-sm text-gray-600">{selectedClient.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 text-gray-500 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Comercial:</p>
                      <p className="text-sm text-gray-600">{selectedClient.business_address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Parcelas Pendentes</h4>
                <div className="space-y-2">
                  {selectedClient.pendingInstallments.map((installment) => (
                    <div
                      key={installment.id}
                      className={`p-3 rounded-md border ${
                        isOverdue(installment.due_date)
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">
                            Vencimento: {formatDate(installment.due_date)}
                          </p>
                          <p className="text-sm text-gray-600">
                            Valor: {formatCurrency(installment.amount)}
                          </p>
                        </div>
                        <div className="text-right">
                          {isOverdue(installment.due_date) && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                              Em atraso
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Parcelas Pagas no Modal */}
              {selectedClient.paidInstallments.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Parcelas Pagas</h4>
                  <div className="space-y-2">
                    {selectedClient.paidInstallments.map((installment) => (
                      <div
                        key={installment.id}
                        className="p-3 rounded-md border bg-green-50 border-green-200"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium">
                              Vencimento: {formatDate(installment.due_date)}
                            </p>
                            <p className="text-sm text-gray-600">
                              Valor: {formatCurrency(installment.amount)}
                            </p>
                            <p className="text-xs text-green-700">
                              Pago em: {installment.paid_at ? 
                                formatDate(installment.paid_at.split('T')[0]) : 
                                installment.payment_date_time ? 
                                  formatDate(installment.payment_date_time.split('T')[0]) : 
                                  'Data não informada'
                              }
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-green-600">
                              {formatCurrency(installment.paid_amount || installment.amount)}
                            </span>
                            {installment.late_fee && installment.late_fee > 0 && (
                              <div className="text-xs text-orange-600">
                                + {formatCurrency(installment.late_fee)} (juros)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}